import React, { useContext, useCallback, useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, GenericInfoComp, SlideOutComp, TimeSeriesType, BaseTimeSeriesInfoProps, getErrorMessage }
    from '@moderepo/mode-app-components';
import { userAppDataContext, selectTimeSeriesInfo, fetchTimeSeriesInfo, fetchTimeSeriesRange, selectTimeSeriesRange, deleteTimeSeries,
    deleteTimeSeriesData } from '@moderepo/mode-data-state-user-app';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById, selectSudoProjectAccessKey }
    from '@moderepo/mode-data-state-ritz';
import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { TimeSeriesInfo, ApiError } from '@moderepo/mode-apis';
import clsx from 'clsx';
import { Agent, AgentRole } from '@moderepo/mode-internal-apis';
import moment from 'moment';
import { useRitzTranslation } from '../../utils/customHooks';
import { RouteHelper } from '../routes/RouteHelper';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { SelectDateRangeDialog } from '../../components';
import { getTimeSeriesTypeName } from './tsUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '&.delete': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});



interface TimeSeriesInfoPreviewContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly moduleId: string;
    readonly seriesId: string;
    readonly context: {
        readonly userId?: number | undefined;
    }
}


/**
 * This Component is used for displaying HomeInfo on the preview panel for preview. It does not show all home details e.g. devices, logs,
 * kv pairs, etc... but only the Home object details
 */
export const TimeSeriesInfoPreviewContainer = (props: TimeSeriesInfoPreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canDeleteTimeSeries = projectAgent?.role === AgentRole.ADMIN;
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const routeHelper = RouteHelper.getInstance(navigate);
    const selectedTimeSeriesInfo = selectTimeSeriesInfo(appDataState, props.homeId, props.moduleId, props.seriesId);
    const timeSeriesRange = selectTimeSeriesRange(appDataState, props.homeId, props.moduleId, props.seriesId);
    const [showSelectDateRangeDialog, setShowSelectDateRangeDialog] = useState<boolean>(false);
    const classes = useStyle();



    /**
     * This useEffect used for loading TimeSeries info
     */
    useEffect(() => {
        if (!selectedTimeSeriesInfo) {
            appDataDispatch(fetchTimeSeriesInfo(props.homeId, props.moduleId, props.seriesId)).catch(() => { /* ignore error */ });
        }
    }, [props.homeId, selectedTimeSeriesInfo, appDataDispatch, props.moduleId, props.seriesId, timeSeriesRange]);



    /**
     * This useEffect used for loading time series data range
     */
    useEffect(() => {
        if (!timeSeriesRange) {
            appDataDispatch(fetchTimeSeriesRange(props.homeId, props.moduleId, props.seriesId), true, true).catch(() => { /* ignore error */ });
        }
    }, [props.homeId, selectedTimeSeriesInfo, appDataDispatch, props.moduleId, props.seriesId, timeSeriesRange]);


    /**
     * Unselect the selected time series to hide the preview panel
     */
    const unselectTimeSeries = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * Take the user to the time series data page
     */
    const viewTimeSeriesData = useCallback((timeSeriesInfo: TimeSeriesInfo) => {
        routeHelper.gotoHomeHistoricalDataPage({
            projectId,
            userId           : props.context.userId,
            homeId           : timeSeriesInfo.homeId,
            tsdbSmartModuleId: timeSeriesInfo.moduleId,
            dataId           : timeSeriesInfo.id,
            timeSeriesType   : TimeSeriesType.SIMPLE,
            dataDisplayName  : timeSeriesInfo.id,
        });
        uiDispatch(hideSlideOutPanel());
    }, [projectId, props.context.userId, routeHelper, uiDispatch]);



    const onDeleteTimeSeriesClick = useCallback((timeSeries: TimeSeriesInfo, errorMessage?: string) => {
        // Confirm before deleting a timeSeries.
        uiDispatch(showDialog({
            type          : DialogType.INPUT,
            title         : 'dialog.generic_confirm_delete.title',
            titleData     : { object_name: timeSeries.id, object_term: 'label.time_series', object_field: 'label.id' },
            message       : 'dialog.generic_confirm_delete.message',
            messageData   : { object_name: timeSeries.id, object_term: trans('label.time_series'), object_field: 'label.id' },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : 'label.id',
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'label.time_series',
                    object_field: 'label.id',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const timeSeriesId = response.input ? response.input.trim() : undefined;

                    if (!timeSeriesId) {
                        // Name is not provided. Call this function again to show the delete timeSeries dialog with the new error
                        onDeleteTimeSeriesClick(timeSeries, 'error.input.generic.empty');
                    } else if (timeSeriesId !== timeSeries.id) {
                        // Name does not match. Call this function again to show the delete timeSeries dialog with the new error
                        onDeleteTimeSeriesClick(timeSeries, 'error.input.generic.invalid');
                    } else {
                        try {
                            // Close the preview panel
                            unselectTimeSeries();

                            // Dispatch an action to delete the timeSeries
                            await appDataDispatch(deleteTimeSeries(timeSeries.homeId, timeSeries.moduleId, timeSeries.id, projectKey?.token));

                            uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                                term: capitalizeAll(trans('label.time_series')),
                            }));
                        } catch (error) {
                            // Show error using notification
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

                            // Call this function again to show the delete timeSeries dialog
                            onDeleteTimeSeriesClick(timeSeries, undefined);
                        }
                    }
                }
            },
        }));
    }, [appDataDispatch, projectKey?.token, trans, uiDispatch, unselectTimeSeries]);



    const deleteDataByDateRange = useCallback(async (
        timeSeries: TimeSeriesInfo, startDate: Date, endDate: Date,
    ) => {
        // NOTE: startDate and endDate are in local time zone therefore, we need to create a new moment TIME ZONE Date object using the
        // provided startDate and endDate's date and time values (WITHOUT time zone) and then use the collection's timeZone.
        const startDateTZ = moment.tz(moment(startDate).format('YYYY-MM-DD HH:mm:ss'), timeSeries.timeZone ?? 'UTC');
        const endDateTZ = moment.tz(moment(endDate).format('YYYY-MM-DD HH:mm:ss'), timeSeries.timeZone ?? 'UTC');

        // Confirm before deleting a time series data.
        uiDispatch(showDialog({
            type     : DialogType.CONFIRM,
            title    : 'dialog.confirm_delete_tsdb_data.title',
            titleData: { series_type: getTimeSeriesTypeName(TimeSeriesType.SIMPLE) },
            message  : trans('dialog.confirm_delete_tsdb_data.message', {
                object_name: timeSeries.id,
                start_date : startDateTZ.format('YYYY-MM-DD HH:mm:ss z'),
                end_date   : endDateTZ.format('YYYY-MM-DD HH:mm:ss z'),
            }),
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    try {
                        // Dispatch an action to delete the time series data
                        await appDataDispatch(deleteTimeSeriesData(
                            timeSeries.homeId, timeSeries.moduleId, timeSeries.id,
                            startDateTZ.toISOString(), endDateTZ.toISOString(),
                            projectKey?.token,
                        ));

                        uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                            term: capitalizeAll(trans('label.time_series_data')),
                        }));

                        setShowSelectDateRangeDialog(false);
                    } catch (error) {
                        // Show error using notification
                        uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                    }
                }
            },
        }));
    }, [appDataDispatch, projectKey?.token, trans, uiDispatch]);


    const primaryActions = useMemo(() => {
        if (selectedTimeSeriesInfo) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('label.view_historical_data')),
                onClick: () => {
                    viewTimeSeriesData(selectedTimeSeriesInfo);
                },
            }];
            return actions;
        }

        return undefined;
    }, [selectedTimeSeriesInfo, trans, viewTimeSeriesData]);



    const secondaryActions = useMemo(() => {
        if (canDeleteTimeSeries && selectedTimeSeriesInfo) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'delete'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.delete_time_series')),
                onClick  : () => {
                    onDeleteTimeSeriesClick(selectedTimeSeriesInfo);
                },
            }, {
                className: clsx(classes.secondaryAction, 'delete'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.delete_time_series_data')),
                onClick  : () => {
                    setShowSelectDateRangeDialog(true);
                },
            }];
            return actions;
        }

        return undefined;
    }, [canDeleteTimeSeries, selectedTimeSeriesInfo, classes.secondaryAction, trans, onDeleteTimeSeriesClick]);


    const slideOutCompContent = useMemo(() => {
        if (selectedTimeSeriesInfo) {
            return (
                <GenericInfoComp<BaseTimeSeriesInfoProps>
                    className="transparent"
                    icon="storage"
                    title={capitalizeAll(trans('panel.time_series_info.title'))}
                    fields={{
                        id: {
                            label: capitalizeAll(trans('label.id')),
                            value: selectedTimeSeriesInfo.id,
                        },
                        homeId: {
                            label    : capitalizeAll(trans('label.home_id')),
                            value    : selectedTimeSeriesInfo.homeId,
                            inputType: 'number',
                        },
                        moduleId: {
                            label: capitalizeAll(trans('label.tsdb_smart_module_id')),
                            value: selectedTimeSeriesInfo.moduleId,
                        },
                        timeZone: {
                            label: capitalizeAll(trans('label.time_zone')),
                            value: selectedTimeSeriesInfo.timeZone,
                        },
                        startDate: {
                            label       : capitalizeAll(trans('label.start_date')),
                            value       : timeSeriesRange?.begin,
                            displayValue: getDateTimeAndElapsedTime(timeSeriesRange?.begin, trans, true),
                        },
                        endDate: {
                            label       : capitalizeAll(trans('label.end_date')),
                            value       : timeSeriesRange?.end,
                            displayValue: getDateTimeAndElapsedTime(timeSeriesRange?.end, trans, true),
                        },
                    }}
                />
            );
        }
        return undefined;
    }, [selectedTimeSeriesInfo, timeSeriesRange, trans]);



    return (
        <>
            {selectedTimeSeriesInfo && (
                <SlideOutComp
                    primaryActions={primaryActions}
                    secondaryActions={secondaryActions}
                    compContent={slideOutCompContent}
                    contentContainerProps={{
                        className: 'slide-out-comp-container',
                    }}
                />
            )}

            {!selectedTimeSeriesInfo && (
                <CircularProgress />
            )}

            {selectedTimeSeriesInfo && (
                <SelectDateRangeDialog
                    projectId={props.projectId}
                    open={showSelectDateRangeDialog}
                    titleKey="label.delete_time_series_data"
                    subtitleKey="label.delete_tsdb_data_message"
                    displayedTimeZone={selectedTimeSeriesInfo.timeZone}
                    onCancel={() => {
                        setShowSelectDateRangeDialog(false);
                    }}
                    onDateRangeSelected={(startDate: Date, endDate: Date) => {
                        deleteDataByDateRange(selectedTimeSeriesInfo, startDate, endDate);
                    }}
                />
            )}
        </>
    );
};
